import { useCallback, useEffect } from 'react'

import {
  useRecoilCallback,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'

import {
  Flex,
  FlexItem,
  Loading,
  ToastContainer,
  Tooltip
} from 'mailup-web-components'

import { getWindowWidth, notifyParent } from 'mailup-react-helpers'

import ErrorFeedback from 'Components/ErrorFeedback'

import CalendarView from './components/CalendarView'
import Header from './components/Header'
import ModalListFilter from './components/ModalListFilter'
import TimelineEmptyState from './components/TimelineEmptyState'
import TimelineSendings from './components/TimelineSendings'
import TimelineTitle from './components/TimelineTitle'

import {
  pageState,
  pecPlusListStatusState
} from 'Pages/ScheduledSendings/recoil/state/page'

import {
  fetchScheduledCallback,
  sendingScheduleSetScheduleCallback,
  sendingSegmentationSimulateCallback,
  stopSendingCampaignConfirmCallback,
  stopSendingConfirmCallback
} from 'Pages/ScheduledSendings/recoil/callbacks/callbacks'

import ModalSendingSegmentation from 'Components/ModalSendingSegmentation'
import ModalStopCampaign from 'Components/ModalStopCampaign'
import ModalStopSending from 'Components/ModalStopSending'
import {
  modalScheduleSendingState,
  modalSendingSegmentationState,
  modalStopCampaignState,
  modalStopSendingState
} from './recoil/state/modals'

import ModalSchedule from 'Components/ModalSchedule'

const MIN_WINDOW_WIDTH = 1550

const ScheduledSendings = () => {
  const reloadPage = useCallback(() => {
    window.location.reload()
  }, [])

  const { loading, loaded, error, severity } = useRecoilValue(pageState)

  const fetchScheduled = useRecoilCallback(fetchScheduledCallback, [])

  useEffect(() => {
    fetchScheduled()
  }, [])

  useEffect(() => {
    const windowWidth = getWindowWidth()

    if (windowWidth < MIN_WINDOW_WIDTH) {
      notifyParent('hideNavMenu', [true])
    }

    return () => {
      notifyParent('restoreNavMenuHidden')
    }
  }, [])

  /* modale di stop */
  const { show, idSending } = useRecoilValue(modalStopSendingState)
  const handleCancel = useResetRecoilState(modalStopSendingState)
  const stopSendingConfirm = useRecoilCallback(stopSendingConfirmCallback, [])
  const handleConfirm = () => {
    stopSendingConfirm(idSending)
  }
  /* ********* */
  /* modale di rischedulazione */
  const {
    show: showModaleSchedule,
    idSending: idSendingSchedule,
    timeZoneCode,
    startDate: scheduleDate,
    severity: severitySchedule,
    error: errorSchedule,
    sending
  } = useRecoilValue(modalScheduleSendingState)
  const pecPlusStatus = useRecoilValue(
    pecPlusListStatusState(idSendingSchedule)
  )
  const dismiss = useResetRecoilState(modalScheduleSendingState)
  const confirm = useRecoilCallback(sendingScheduleSetScheduleCallback, [])
  /* ********* */

  /* modale scelta destinatari */
  const {
    show: showModaleSegmentation,
    error: errorModaleSegmentation,
    severity: severityModaleSegmentation,
    loading: loadingModaleSegmentation,
    groups,
    excludedGroups,
    filters,
    section,
    queued,
    simulationEnabled,
    simulationLoading,
    simulationLoaded,
    simulationResult,
    simulationDiff
  } = useRecoilValue(modalSendingSegmentationState)
  const setModalSegmentationState = useSetRecoilState(
    modalSendingSegmentationState
  )
  const closeModalSegmentation = useResetRecoilState(
    modalSendingSegmentationState
  )
  const handleSimulate = useRecoilCallback(
    sendingSegmentationSimulateCallback,
    []
  )
  /* ********* */
  /* modale di stop campagna */
  const {
    show: showModalStopCampaignState,
    idList,
    idSending: idSendingModalStopCampaignState,
    idSource,
    mode
  } = useRecoilValue(modalStopCampaignState)
  const closeModal = useResetRecoilState(modalStopCampaignState)
  const handleEdit = () => {
    closeModal()
    notifyParent('setCurrentList', [
      idList,
      `App.aspx#/contentsSourcesAddEdit/${idSource}`,
      true
    ])
  }
  const stopSendingCampaignConfirm = useRecoilCallback(
    stopSendingCampaignConfirmCallback,
    []
  )

  const handleConfirmStopCampaignState = () => {
    stopSendingCampaignConfirm(idSendingModalStopCampaignState)
  }
  /* ********* */

  return (
    <>
      <Tooltip />
      <ToastContainer />
      <Loading open={loading} />
      <Header />
      <ModalListFilter />
      <ModalStopSending
        show={show}
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
        appendToDataMuTest='scheduled-sendings'
      />
      <ModalSendingSegmentation
        show={showModaleSegmentation}
        error={errorModaleSegmentation}
        severity={severityModaleSegmentation}
        loading={loadingModaleSegmentation}
        groups={groups}
        excludedGroups={excludedGroups}
        filters={filters}
        section={section}
        closeModal={closeModalSegmentation}
        setModalState={setModalSegmentationState}
        queued={queued}
        simulationEnabled={simulationEnabled}
        simulationLoading={simulationLoading}
        simulationLoaded={simulationLoaded}
        simulationResult={simulationResult}
        simulationDiff={simulationDiff}
        handleSimulate={handleSimulate}
      />
      <ModalStopCampaign
        show={showModalStopCampaignState}
        idList={idList}
        idSending={idSendingModalStopCampaignState}
        idSource={idSource}
        mode={mode}
        handleEdit={handleEdit}
        handleConfirm={handleConfirmStopCampaignState}
        closeModal={closeModal}
        appendToDataMuTest='scheduled-sendings'
      />
      <ModalSchedule
        show={showModaleSchedule}
        timeZoneCode={timeZoneCode}
        idSending={idSendingSchedule}
        scheduleDate={scheduleDate}
        error={errorSchedule}
        severity={severitySchedule}
        dismiss={dismiss}
        confirm={confirm}
        pecPlusStatus={pecPlusStatus}
        sending={sending}
      />
      <div className='l-container'>
        <ErrorFeedback
          error={error}
          severity={severity}
          onClickReload={reloadPage}
          className='l-scheduled-sendings__error-feedback'
        />
        {!error && (loaded || true) && (
          <Flex margin='0 0 24px 0'>
            <FlexItem flex='1 0 calc(75% - 15px)' padding='0 16px 0 0'>
              <CalendarView />
            </FlexItem>
            <FlexItem flex='1 0 calc(25% - 15px)' padding='0 0 0 16px'>
              <TimelineTitle />
              <TimelineEmptyState />
              <TimelineSendings />
            </FlexItem>
          </Flex>
        )}
      </div>
    </>
  )
}

export default ScheduledSendings
